import React from "react";
import cx from "classnames";
import { DateView } from "./DateView";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { type DateRateFomatted } from "apis/calendarRates";

type MonthViewProps = {
  month: Dayjs;
  datePropsFn: (date: Dayjs) => {
    selected?: boolean;
    inBetweenSelected?: boolean;
    color?: "BLUE" | "GREEN";
    disabled?: boolean;
  };
  onHoverDate: (date: Dayjs) => void;
  onClickDate: (date: Dayjs) => void;
  plainAndScrollable: boolean;
  dateRate: Record<string, DateRateFomatted> | null;
};

const datesForAMonth = (month: Dayjs) => {
  const start = month.startOf("month");
  const end = month.endOf("month");

  let iterator = start;
  let dates = [];

  while (iterator.isBefore(end)) {
    dates.push(iterator);
    iterator = iterator.add(1, "day");
  }

  return dates;
};

const groupDatesByDays = (dates: Dayjs[]) => {
  const offset = dates[0].day(); // Day of Sunday is 0
  const paddedDates = [...Array(offset), ...dates];

  // https://stackoverflow.com/a/60779547
  return paddedDates.reduce((acc, _, i) => {
    if (i % 7 === 0) acc.push(paddedDates.slice(i, i + 7));
    return acc;
  }, []);
};

export const MonthView = ({
  month,
  datePropsFn,
  onHoverDate,
  onClickDate,
  plainAndScrollable,
  dateRate,
}: MonthViewProps) => {
  const days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const dates = datesForAMonth(month);
  const groupedDates = groupDatesByDays(dates);

  return (
    <div className={cx({ "py-20": !plainAndScrollable })}>
      <div className="font-cormorant font-semibold text-m-h4 pb-20 text-center">
        {month.format("MMMM YYYY")}
      </div>
      <table className={cx("border-collapse", { hidden: plainAndScrollable })}>
        <tbody>
          <tr className="font-mulish font-bold text-gold-disabled flex flex-grow-0">
            {days.map((day, index) => (
              <td key={index} className="inline-block w-39 h-39 text-center">
                <small>{day}</small>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <table className="border-collapse flex justify-end">
        <tbody>
          {groupedDates.map((dates: Dayjs[], index: number) => (
            <tr key={index}>
              {dates.map((date, index) => {
                return (
                  <DateView
                    date={date}
                    datePropsFn={datePropsFn}
                    onHover={onHoverDate}
                    onClick={onClickDate}
                    plainView={plainAndScrollable}
                    dateRate={
                      dateRate
                        ? dateRate[dayjs(date).format("YYYY-MM-DD")]
                        : null
                    }
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Not sure what to name `plainAndScrollable`. This view/design/theme is only
// used for a range picker on mobile. That drawer which opens up
